import {useMutation, useQuery} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {dateFormatFR} from '../../../utils';
import ColoredHeader from '../../../common/components/layout/ColoredHeader';
import MandatoryInput from '../../../common/components/layout/MandatoryInput';
import SelectCivility from '../../../common/components/User/SelectCivility';
import ProfileFooter from './ProfileFooter';

const ContainerRow = styled(Row)`
    align-items: baseline;
`;

const ContentCol = styled(Col)`
  margin-bottom: 20px;
`;

const GRAPHQL_MUTATION_UPDATE_CENTRAL_PURCHASING = gql`
  mutation updateCentralPurchasing($userInput: UpdateUserInput!, $centralPurchasingInput: UpdateCentralPurchasingInput!) {
    updateCentralPurchasing(userInput: $userInput, centralPurchasingInput: $centralPurchasingInput) {
      id
      firstName
      lastName
      email
      postalCode
      street
      city
      centralPurchasing{
        id
        brand {
          id
          name
        }
        contactEmail
        contactName
        siret
      }
    }
  }
`;

const GRAPHQL_QUERY_CENTRAL_PURCHASING = gql`
    {
        currentUser {
            id
            firstName
            lastName
            civility
            birthDate
            email
            mobilePhone
            street
            postalCode
            city
            centralPurchasing{
              id
              contactEmail
              siret
              brand{
                id
                name
              }
            }
        }
    }
`;

const initialInfos = {
  user: {
    firstName: '',
    lastName: '',
    civility: '',
    email: '',
    mobilePhone: '',
    birthDate: '',
    street: '',
    postalCode: '',
    city: '',
  },
  centralPurchasing: {
    contactEmail: '',
    siret: '',
  },
  brand: {
    name: '',
  },
};

const InputCol = props => <Col xs={6} xl={6} {...props}/>;

const ProfileCentral = ({t, showSuccess, showError}) => {
  const [infos, setInfos] = useState(initialInfos);
  const {data} = useQuery(GRAPHQL_QUERY_CENTRAL_PURCHASING);
  const [mutation, {loading}] = useMutation(GRAPHQL_MUTATION_UPDATE_CENTRAL_PURCHASING);

  useEffect(() => {
    if (data && data.currentUser) {
      delete data.currentUser.__typename;
      delete data.currentUser.centralPurchasing.id;
      const {centralPurchasing, ...user} = data.currentUser;
      setInfos(infos => ({
        ...infos,
        centralPurchasing,
        user,
        brand: centralPurchasing.brand,
      }));
    }
  }, [data]);

  const setState = key => {
    return function(name, value) {
      setInfos({
        ...infos,
        [key]: {
          ...infos[key],
          [name]: value,
        },
      });
    };
  };

  const onSubmit = () => {

    const centralPurchasingInput = {
      contactEmail: infos.centralPurchasing.contactEmail,
      siret: infos.centralPurchasing.siret,
    };

    const variables = {
      userInput: infos.user,
      centralPurchasingInput,
    };

    mutation({variables})
      .then(() => {
        showSuccess();
      })
      .catch((error) => {
        console.error(error);
        showError();
      });
  };

  return (
    <>
      <ContainerRow>
        <ContentCol xs={12}>
          <Row className="section-info row">
            <ColoredHeader>{t('centralPurchasing.informations')}</ColoredHeader>
            <InputCol>
              <MandatoryInput
                title={`${t('dataTable.firstname')} : `}
                name="firstName"
                value={infos.user.firstName}
                onChange={setState('user')}
                show={infos.user.firstName === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('dataTable.lastname')} : `}
                name="lastName"
                value={infos.user.lastName}
                onChange={setState('user')}
                show={infos.user.lastName === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                type="date"
                title={`${t('dataTable.birthdate')} : `}
                name="birthDate"
                value={infos.user.birthDate}
                onChange={setState('user')}
                show={infos.user.birthDate === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('dataTable.civility')} : `}
                name="civility"
                value={infos.user.civility}
                Component={SelectCivility}
                onChange={setState('user')}
                show={infos.user.civility === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                disabled
                title={`${t('dataTable.email')} : `}
                name="email"
                value={infos.user.email}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.phoneNumber')} : `}
                name="mobilePhone"
                value={infos.user.mobilePhone}
                onChange={setState('user')}
                show={infos.user.mobilePhone === ''}/>
            </InputCol>
          </Row>
        </ContentCol>

        <ContentCol xs={12}>
          <Row className="section-info row">
            <ColoredHeader>{t('centralPurchasing.business')}</ColoredHeader>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.brandName')} : `}
                name="brand"
                value={infos.brand.name}
                disabled/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.createdAt')} : `}
                name="createdAt"
                value={dateFormatFR({date: infos.centralPurchasing.createdAt})}
                disabled/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.contactEmail')} : `}
                name="contactEmail"
                value={infos.centralPurchasing.contactEmail}
                onChange={setState('centralPurchasing')}
                show={infos.centralPurchasing.contactEmail === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.siret')} : `}
                name="siret"
                value={infos.centralPurchasing.siret}
                onChange={setState('centralPurchasing')}
                show={infos.centralPurchasing.siret === ''}/>
            </InputCol>

            <InputCol xs={5}>
              <MandatoryInput
                title={`${t('centralPurchasing.postalCode')} : `}
                name="postalCode"
                value={infos.user.postalCode}
                onChange={setState('user')}
                show={infos.user.postalCode === ''}/>
            </InputCol>
            <InputCol xs={7}>
              <MandatoryInput
                title={`${t('centralPurchasing.street')} : `}
                name="street"
                value={infos.user.street}
                onChange={setState('user')}
                show={infos.user.street === ''}/>
            </InputCol>
            <InputCol>
              <MandatoryInput
                title={`${t('centralPurchasing.city')} : `}
                name="city"
                value={infos.user.city}
                onChange={setState('user')}
                show={infos.user.city === ''}/>
            </InputCol>
          </Row>
        </ContentCol>
      </ContainerRow>
      <ProfileFooter onClick={onSubmit} loading={loading}/>
    </>
  );
};

ProfileCentral.propTypes = {
  t: PropTypes.func.isRequired,
  showSuccess: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
};

export default withTranslation('user')(ProfileCentral);
